type DetachedIssueLevel = 'warn' | 'error';

export type DetachedIssueEvent =
  | 'reserve_failed'
  | 'popup_blocked'
  | 'fallback_to_main_view'
  | 'fallback_timeout'
  | 'join_failed'
  | 'focus_failed';

export interface DetachedIssueEntry {
  ts: number;
  event: DetachedIssueEvent;
  data?: Record<string, unknown>;
}

const MAX_ISSUES = 100;

// We keep a full ring-buffer for QA, but only log the most important events by default.
const DEFAULT_CONSOLE_EVENTS: Record<DetachedIssueEvent, boolean> = {
  reserve_failed: false,
  popup_blocked: true,
  fallback_to_main_view: false, // implicit from popup_blocked in current flows
  fallback_timeout: true,
  join_failed: true,
  focus_failed: true,
};

const CONSOLE_DEDUPE_MS: Partial<Record<DetachedIssueEvent, number>> = {
  // Prevent noisy repeats if React mounts/unmounts quickly or a flow retries.
  popup_blocked: 2000,
  fallback_to_main_view: 2000,
  reserve_failed: 2000,
  focus_failed: 2000,
  fallback_timeout: 5000,
  // join_failed is an error; don't dedupe it.
};

const lastConsoleLogTsByKey: Map<string, number> = new Map();

function getBuffer(): DetachedIssueEntry[] {
  try {
    const w = window as any;
    if (!Array.isArray(w.__HS_DETACHED_ISSUES)) {
      w.__HS_DETACHED_ISSUES = [];
    }
    return w.__HS_DETACHED_ISSUES as DetachedIssueEntry[];
  } catch {
    return [];
  }
}

function push(entry: DetachedIssueEntry): void {
  const buf = getBuffer();
  buf.push(entry);
  if (buf.length > MAX_ISSUES) {
    buf.splice(0, buf.length - MAX_ISSUES);
  }
}

function getConsoleDedupeKey(event: DetachedIssueEvent, data?: Record<string, unknown>): string {
  const roomId = typeof data?.roomId === 'string' ? (data?.roomId as string) : '';
  return roomId ? `${event}:${roomId}` : event;
}

function shouldLogToConsole(event: DetachedIssueEvent): boolean {
  // If popup debug is enabled (dev only), log everything; otherwise keep console minimal.
  try {
    const w = window as any;
    if (typeof w.__HS_POPUP_DEBUG === 'boolean' && w.__HS_POPUP_DEBUG) return true;
  } catch {
    // ignore
  }
  return DEFAULT_CONSOLE_EVENTS[event] === true;
}

// Always-on issue reporting for QA: keep it concise and consistent.
export function reportDetachedIssue(
  event: DetachedIssueEvent,
  data?: Record<string, unknown>,
  level: DetachedIssueLevel = 'warn'
): void {
  const entry: DetachedIssueEntry = { ts: Date.now(), event, data };
  push(entry);

  try {
    if (!shouldLogToConsole(event)) return;

    // Dedupe only warn-level logs (errors should always print).
    const dedupeMs = level === 'error' ? 0 : (CONSOLE_DEDUPE_MS[event] || 0);
    if (dedupeMs > 0) {
      const key = getConsoleDedupeKey(event, data);
      const last = lastConsoleLogTsByKey.get(key) || 0;
      if (entry.ts - last < dedupeMs) return;
      lastConsoleLogTsByKey.set(key, entry.ts);
    }

    const prefix = `[hsDetached] ${event}`;
    if (level === 'error') {
      // Use a single structured log line so QA can copy/paste.
      console.error(prefix, data || {});
    } else {
      console.warn(prefix, data || {});
    }
  } catch {
    // ignore
  }
}

export function installDetachedIssueTools(): void {
  try {
    const w = window as any;
    if (w.hsDetached) return;
    w.hsDetached = {
      help: () => {
        console.log('hsDetached commands:');
        console.log('- hsDetached.getIssues()');
        console.log('- hsDetached.clearIssues()');
        console.log('- hsDetached.dumpIssues()');
        return true;
      },
      getIssues: (): DetachedIssueEntry[] => getBuffer().slice(),
      clearIssues: (): boolean => {
        const buf = getBuffer();
        buf.splice(0, buf.length);
        return true;
      },
      dumpIssues: (): string => {
        const issues = getBuffer();
        const text = JSON.stringify(issues, null, 2);
        console.log(text);
        return text;
      },
    };
  } catch {
    // ignore
  }
}
